'use client';

import H2 from '@/components/typography/h2';
import H3 from '@/components/typography/h3';
import H5 from '@/components/typography/h5';
import P from '@/components/typography/p';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { countDecimals, formatNumber, isNumeric, toNumber } from '@/lib/utils';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

type Stat = {
    label: string;
    value: string | number;
    suffix?: string;
    formatDecimals?: boolean;
};

export default function Certified() {
    const { t } = useTranslation();

    const stats = t('certified.stats', { returnObjects: true }) as Stat[];
    const footerVal = t('certified.footer') as string | number;

    const containerRef = useRef<HTMLDivElement | null>(null);
    const valueRefs = useRef<(HTMLSpanElement | null)[]>([]);
    const footerRef = useRef<HTMLSpanElement | null>(null);

    const numericIndices = useMemo(
        () => stats.map((s) => isNumeric(s.value)),
        [stats],
    );
    const footerIsNumeric = useMemo(() => isNumeric(footerVal), [footerVal]);

    useEffect(() => {
        if (!containerRef.current) return;

        numericIndices.forEach((isNum, idx) => {
            const el = valueRefs.current[idx];
            if (isNum && el) el.textContent = '0';
        });
        if (footerIsNumeric && footerRef.current) {
            footerRef.current.textContent = '0';
        }

        const animations: gsap.core.Tween[] = [];

        const trigger = ScrollTrigger.create({
            trigger: containerRef.current,
            start: 'top 75%',
            once: true,
            onEnter: () => {
                numericIndices.forEach((isNum, idx) => {
                    if (!isNum) return;
                    const el = valueRefs.current[idx];
                    if (!el) return;

                    if (!stats || !stats[idx]) return;

                    const rawVal = toNumber(stats[idx].value)!;
                    const formatDecimals = Boolean(stats[idx].formatDecimals);
                    const decimals = Number.isInteger(rawVal)
                        ? 0
                        : Math.min(3, countDecimals(rawVal));

                    const counter = { v: 0 };
                    const tween = gsap.to(counter, {
                        v: rawVal,
                        duration: 4,
                        ease: 'power2.out',
                        onUpdate: () => {
                            if (!el) return;
                            const current =
                                decimals === 0
                                    ? Math.round(counter.v)
                                    : Number(counter.v.toFixed(decimals));
                            el.textContent = formatDecimals
                                ? formatNumber(current, decimals)
                                : String(current);
                        },
                    });
                    animations.push(tween);
                });

                if (footerIsNumeric && footerRef.current) {
                    const rawVal = toNumber(footerVal)!;
                    const decimals = Number.isInteger(rawVal)
                        ? 0
                        : Math.min(3, countDecimals(rawVal));
                    const counter = { v: 0 };
                    const tween = gsap.to(counter, {
                        v: rawVal,
                        duration: 4,
                        ease: 'power2.out',
                        onUpdate: () => {
                            if (!footerRef.current) return;
                            const current =
                                decimals === 0
                                    ? Math.round(counter.v)
                                    : Number(counter.v.toFixed(decimals));
                            footerRef.current.textContent = formatNumber(
                                current,
                                decimals,
                            );
                        },
                    });
                    animations.push(tween);
                }
            },
        });

        return () => {
            trigger.kill();
            animations.forEach((tw) => tw.kill());
        };
    }, [numericIndices, stats, footerIsNumeric, footerVal]);

    return (
        <div>
            <div
                ref={containerRef}
                className="mt-12 flex flex-col items-center pt-2 xl:mt-[6rem] xl:flex-row-reverse xl:justify-center xl:gap-12 xl:pl-[4rem]"
            >
                <div className="bg-lightpurple flex min-h-[22.5rem] w-[20.87rem] transform-[rotate(2deg)] flex-col rounded-lg lg:min-h-[22.5rem] lg:w-[48rem]">
                    <div className="w-full border-b border-black pb-2">
                        <div className="px-5 py-8 pb-3 xl:mx-auto xl:flex xl:px-0 xl:py-[3.25rem] xl:pb-[2.25rem]">
                            <H2
                                variant="italic"
                                className="italic xl:mx-auto xl:text-center"
                            >
                                {t('certified.title')}
                            </H2>
                        </div>
                    </div>

                    {stats.map((stat, index) => (
                        <div
                            key={index}
                            className="border-primary flex border-b"
                        >
                            <div className="border-primary flex w-full border-r px-5 py-3 xl:w-1/2 xl:px-16 xl:py-2.5">
                                <H5>{stat.label}</H5>
                            </div>
                            <div className="flex w-3/5 px-5 py-3 xl:w-1/2 xl:py-2.5">
                                <H5 className="normal-case">
                                    <span
                                        ref={(el) => {
                                            valueRefs.current[index] = el;
                                        }}
                                        aria-label={String(stat.value)}
                                    >
                                        {isNumeric(stat.value)
                                            ? '0'
                                            : String(stat.value)}
                                    </span>
                                    <span>{stat.suffix}</span>
                                </H5>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="xl:max-w=[51rem] xl:w-1/2">
                    <div className="mt-[3.5rem] px-5">
                        <P>{t('certified.description')}</P>
                    </div>
                    <div className="mt-[2.5rem] mb-[3.75rem] px-5">
                        <H3 className="mb-[1.25rem]">{t('force.title')}</H3>
                        <P>{t('force.description')}</P>
                    </div>
                </div>
            </div>
        </div>
    );
}
